<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';

type CdnImageWidth = 400 | 800 | 1200 | 1600 | 2400;

export interface ResponsiveImageProps {
  /** CDN image URL. Non-CDN, local, data, and blob URLs render unchanged. */
  src: string;
  /** Alt text for meaningful images. Leave empty only for decorative images. */
  alt?: string;
  /** Subset of the widths supported by the Shopexperts CDN. */
  widths?: number[];
  /** The rendered slot width at each viewport breakpoint. */
  sizes?: string;
  /** Use as an absolutely positioned decorative background image. */
  background?: boolean;
  /** Native browser loading strategy. Images default to lazy loading. */
  loading?: 'eager' | 'lazy';
  /** Native browser fetch priority for critical images. */
  fetchpriority?: 'high' | 'low' | 'auto';
  /** Native browser decoding strategy. */
  decoding?: 'async' | 'sync' | 'auto';
  /** Optional CDN image quality. */
  quality?: 75 | 85 | 90;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  objectPosition?: string;
}

const props = withDefaults(defineProps<ResponsiveImageProps>(), {
  alt: '',
  widths: () => [400, 800, 1200, 1600, 2400],
  sizes: '100vw',
  background: false,
  loading: 'lazy',
  fetchpriority: 'auto',
  decoding: 'async',
  objectFit: 'cover',
  objectPosition: 'center',
});

const SHOPEXPERTS_CDN_IMAGE_WIDTHS: readonly CdnImageWidth[] = [400, 800, 1200, 1600, 2400];

const imageElement = useTemplateRef<{ complete: boolean; naturalHeight: number }>('imageElement');
const hasTransformError = ref(false);

function isLoaded(): boolean {
  return !!imageElement.value?.complete && imageElement.value.naturalHeight !== 0;
}

defineExpose({ isLoaded });

function isShopexpertsCdnUrl(src: string): boolean {
  try {
    return new URL(src).hostname.toLowerCase() === 'cdn.shopexperts.com';
  } catch {
    return false;
  }
}

function withCdnTransform(src: string, width: CdnImageWidth): string {
  const url = new URL(src);
  url.searchParams.set('width', String(width));
  if (props.quality) url.searchParams.set('quality', String(props.quality));
  return url.toString();
}

const responsiveWidths = computed<CdnImageWidth[]>(() => {
  const requestedWidths = new Set(props.widths);
  const supportedWidths = SHOPEXPERTS_CDN_IMAGE_WIDTHS.filter((width) => requestedWidths.has(width));

  return supportedWidths.length ? supportedWidths : [...SHOPEXPERTS_CDN_IMAGE_WIDTHS];
});

const canTransform = computed(() => isShopexpertsCdnUrl(props.src));

const srcset = computed(() => {
  if (!canTransform.value || hasTransformError.value) return undefined;

  return responsiveWidths.value
    .map((width) => `${withCdnTransform(props.src, width)} ${width}w`)
    .join(', ');
});

const defaultSrc = computed(() => {
  if (!canTransform.value || hasTransformError.value) return props.src;

  return withCdnTransform(props.src, responsiveWidths.value.at(-1)!);
});

watch(() => props.src, () => {
  hasTransformError.value = false;
});

function useOriginalOnTransformError(): void {
  if (!canTransform.value || hasTransformError.value) return;

  hasTransformError.value = true;
}

const imageClasses = computed(() => props.background ? 'responsive-image--background' : undefined);

const imageStyles = computed(() => ({
  objectFit: props.objectFit,
  objectPosition: props.objectPosition,
}));
</script>

<template>
  <img
    ref="imageElement"
    :src="defaultSrc"
    :srcset="srcset"
    :sizes="srcset ? props.sizes : undefined"
    :alt="props.alt"
    :aria-hidden="props.background || !props.alt ? 'true' : undefined"
    :loading="props.loading"
    :fetchpriority="props.fetchpriority"
    :decoding="props.decoding"
    :class="imageClasses"
    :style="imageStyles"
    @error="useOriginalOnTransformError"
  />
</template>

<style scoped>
.responsive-image--background {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  pointer-events: none;
}
</style>
